import xCircle from '@iconify/icons-heroicons-solid/x-circle';
import icon3DotsScaleMiddle from '@iconify/icons-svg-spinners/3-dots-scale-middle';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { BFILogoHorizontal } from '@assets/images/svg';
import MemoizedIcon from '@components/MemoizedIcon';
import { ErrorView, PlaceholderView } from '@components/PlaceholderView';
import { EventName } from '@customTypes/Analytics';
import { ProductId, RolesWithProduct } from '@customTypes/api/Commons';
import { SpvDownline } from '@customTypes/Commons';
import useGetEmployeeDetail from '@hooks/reactQueryHooks/employeeDetail/useGetEmployeeDetail';
import useGetIAMAssignedPermission from '@hooks/reactQueryHooks/getIAMAssignedPermission';
import useGetUserMarketingId from '@hooks/reactQueryHooks/useGetUserMarketingId';
import useDatadogTrackerEffect from '@hooks/useDatadogTrackerEffect';
import useFeatureFlags from '@hooks/useFeatureFlags';
import useLocalStorage, { STORAGE_KEY } from '@hooks/useLocalStorage';
import { logEvent, setUserId, setUserProperties } from '@service/firebase';
import { SPV_DOWNLINE_BY_PRODUCT_ID } from '@src/constants';
import { useAuthenticationContext } from '@src/context/AuthenticationContext';
import { getClientInfo } from '@src/utils/clientDetector';
import { parseValue } from '@src/utils/utils';

import SelectMarketingId from './components/SelectMarketingId';
import { AUTHORIZED_ROLES } from './constants';
import { Roles } from './types';

interface AuthorizationContextProps {
  isAllowed: (name: string, rule?: string) => boolean;
  emp_no: string;
  emp_name: string;
  corporate_email: string;
  marketing_id: string;
  job_title_name: string;
  isLoading: boolean;
  rolesProduct: RolesWithProduct;
  productId: ProductId;
  spvDownlineName: string;
  handleSelectMarketingId: (marketingId: string) => void;
  branch_id: string;
  branch_name: string;
  isSPV: boolean;
}
const AuthorizationContext = createContext<AuthorizationContextProps>({
  isAllowed: (_name: string, _rule?: string) => false,
  emp_no: '',
  emp_name: '',
  corporate_email: '',
  marketing_id: '',
  job_title_name: '',
  isLoading: false,
  rolesProduct: RolesWithProduct.None,
  productId: ProductId.None,
  spvDownlineName: SpvDownline.are,
  handleSelectMarketingId: (_marketingId: string) => false,
  branch_id: '',
  branch_name: '',
  isSPV: false,
});

const AuthorizationContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const { features } = useFeatureFlags();

  const { getStorage, setStorage } = useLocalStorage();

  const { doLogout } = useAuthenticationContext();

  const {
    data: permissionData,
    isLoading: isPermissionDataLoading,
    isError: isPermissionDataError,
    refetch: refetchPermissionData,
  } = useGetIAMAssignedPermission();

  const {
    data: employeeDetail,
    isLoading: isEmployeeDetailLoading,
    isError: isEmployeeDetailError,
    refetch: refetchEmployeeDetail,
  } = useGetEmployeeDetail(!isPermissionDataLoading);

  const currentRole = useMemo(() => {
    return employeeDetail
      ? (`agency.spvcockpit.${employeeDetail?.role.toLowerCase()}` as Roles)
      : Roles.None;
  }, [employeeDetail]);

  const isAuthorized =
    permissionData?.role && AUTHORIZED_ROLES.includes(currentRole);

  // For SPV & BM only
  const needToSelectMarketingId = useMemo(
    () =>
      features.selectMarketingId() &&
      [Roles.Spv, Roles.Bm].includes(currentRole) &&
      !getStorage(STORAGE_KEY.MARKETING_ID()),
    [currentRole, features, getStorage],
  );

  const {
    data: userMarketingIdData = [],
    isLoading: isUserMarketingIdLoading,
    isError: isUserMarketingIdError,
    refetch: refetchUserMarketingId,
  } = useGetUserMarketingId(
    needToSelectMarketingId && !isEmployeeDetailLoading,
  );

  const isAllowed = useCallback(
    (name: string, type = 'view') =>
      !!permissionData?.permission &&
      !!Object.keys(permissionData.permission).find(rule => {
        if (permissionData.permission[rule] !== currentRole) return false;
        const ruleKeys = rule
          .replace(/agency\.spvcockpit(\.bm)?\./g, '')
          .split('.');
        const ruleType = ruleKeys.pop();
        return ruleKeys.join('.') === name && ruleType === type;
      }),
    [permissionData?.permission, currentRole],
  );

  const isLoading =
    isEmployeeDetailLoading ||
    isPermissionDataLoading ||
    isUserMarketingIdLoading;

  const isError =
    isEmployeeDetailError || isPermissionDataError || isUserMarketingIdError;

  const refetch = useCallback(() => {
    isEmployeeDetailError && refetchEmployeeDetail();
    isPermissionDataError && refetchPermissionData();
    isUserMarketingIdError && refetchUserMarketingId();
  }, [
    isEmployeeDetailError,
    refetchEmployeeDetail,
    isPermissionDataError,
    refetchPermissionData,
    isUserMarketingIdError,
    refetchUserMarketingId,
  ]);

  const handleSelectMarketingId = useCallback(
    (marketingId: string) => {
      setStorage(STORAGE_KEY.MARKETING_ID(), marketingId);
      refetchEmployeeDetail();
    },
    [refetchEmployeeDetail, setStorage],
  );

  const productId = parseValue(employeeDetail?.product_id, '');

  const spvDownlineName = useMemo(
    () =>
      parseValue(
        SPV_DOWNLINE_BY_PRODUCT_ID[
          [currentRole, productId].filter(Boolean).join('.') as RolesWithProduct
        ],
        SpvDownline.are,
      ),
    [productId, currentRole],
  );

  const renderContent = useCallback(() => {
    if (isLoading)
      return (
        <>
          <MemoizedIcon
            icon={icon3DotsScaleMiddle}
            className="text-primary-500 text-[80px] mt-auto"
          />
          <div className="text-lg mt-8 mb-auto text-center">
            {t('authGate.redirecting')}
          </div>
        </>
      );

    if (isError) {
      return <ErrorView retry={refetch} containerClassName="m-auto" />;
    }

    if (!isAuthorized)
      return (
        <PlaceholderView
          icon={xCircle}
          iconContainerClassName="bg-danger-50 text-danger-500"
          mainText={t('authGate.unauthorizedMainText')}
          subText={t('authGate.unauthorizedSubText')}
          actionText={t('authGate.loginWithOtherAccount')}
          action={doLogout}
          containerClassName="m-auto"
        />
      );

    return <SelectMarketingId />;
  }, [doLogout, isAuthorized, isLoading, t, isError, refetch]);

  useDatadogTrackerEffect(employeeDetail);

  useEffect(() => {
    if (employeeDetail) {
      const clientData = getClientInfo();
      setUserId(employeeDetail?.emp_no);
      setUserProperties({
        name: employeeDetail?.emp_name,
        role: currentRole.replace('agency.spvcockpit.', ''),
        email: employeeDetail?.corporate_email,
        marketing_id: employeeDetail?.marketing_id,
        product: employeeDetail?.product_id,
        branch_code: employeeDetail?.branch_id,
        device_os: clientData.os,

        browser_type: clientData.browser,
        device_type: clientData.deviceType,
      });
      logEvent(EventName.UserLogin);
    }
  }, [employeeDetail, currentRole]);

  const contextValue = useMemo(() => {
    const product_id = ![
      Roles.HoApprover,
      Roles.HoEditor,
      Roles.HoViewer,
      Roles.Abpm,
      Roles.Am,
      Roles.Bm,
    ].includes(currentRole)
      ? parseValue(employeeDetail?.product_id, ProductId.None)
      : ProductId.None;

    const rolesProduct = [currentRole, product_id]
      .filter(Boolean)
      .join('.') as RolesWithProduct;

    const isSPV = [
      RolesWithProduct['SPV.2W'],
      RolesWithProduct['SPV.4W'],
      RolesWithProduct['SPV.PBF'],
    ].includes(rolesProduct);

    return {
      emp_no: parseValue(employeeDetail?.emp_no, ''),
      emp_name: parseValue(employeeDetail?.emp_name, ''),
      corporate_email: parseValue(employeeDetail?.corporate_email, ''),
      marketing_id: parseValue(employeeDetail?.marketing_id, ''),
      job_title_name: parseValue(employeeDetail?.job_title_name, ''),
      job_title_code: parseValue(employeeDetail?.job_title_code, ''),
      branch_id: parseValue(employeeDetail?.branch_id, ''),
      branch_name: parseValue(employeeDetail?.branch_name, ''),
      isLoading,
      rolesProduct: rolesProduct,
      productId: product_id,
      isAllowed,
      spvDownlineName,
      handleSelectMarketingId,
      isSPV,
    };
  }, [
    currentRole,
    employeeDetail?.product_id,
    employeeDetail?.emp_no,
    employeeDetail?.emp_name,
    employeeDetail?.corporate_email,
    employeeDetail?.marketing_id,
    employeeDetail?.job_title_name,
    employeeDetail?.job_title_code,
    employeeDetail?.branch_id,
    employeeDetail?.branch_name,
    isLoading,
    isAllowed,
    spvDownlineName,
    handleSelectMarketingId,
  ]);

  return (
    <AuthorizationContext.Provider value={contextValue}>
      {isAuthorized &&
      !isLoading &&
      (!needToSelectMarketingId ||
        (needToSelectMarketingId && userMarketingIdData.length <= 1)) ? (
        children
      ) : (
        <div className="flex flex-col w-full h-screen">
          <div className="bg-white sm:rounded-none h-[67px] sm:h-[77px] fixed w-full z-50 shadow-[0_1px_4px_rgba(0,0,0,0.08)]">
            <img
              src={BFILogoHorizontal}
              alt="BFILogoHorizontal"
              width={123}
              height={35}
              className="h-full m-auto"
            />
          </div>
          <div className="flex flex-col h-full pt-[67px] sm:pt-[77px]">
            <div className="flex flex-col h-full px-5 sm:px-0 sm:mx-10 sm:mt-[69px] sm:rounded-t-lg bg-white items-center">
              {renderContent()}
            </div>
          </div>
        </div>
      )}
    </AuthorizationContext.Provider>
  );
};

const useAuthorizationContext = () => useContext(AuthorizationContext);

export {
  AuthorizationContext,
  AuthorizationContextProvider,
  useAuthorizationContext,
};
